import type { MiningSnapshot } from "../../../mining-engine";
import { estimateMiningTimeMs } from "../../../mining-engine/estimate";
import type { WalletSessionSnapshot } from "../../../wallet/wallet-session";
import { sameAddress } from "../../../state/model-helpers";
import type { MiningView, MiningPhase } from "../types";

export function miningView(snapshot: MiningSnapshot, enabled: boolean, wallet: WalletSessionSnapshot,
  activeToken: string | null, globalDifficultyBits: string | null = null): MiningView {
  const phaseMap: Record<MiningSnapshot["phase"], MiningPhase> = {
    IDLE: enabled ? "READY" : "LOCKED",
    CHALLENGE_READY: "READY",
    ACQUIRING_TAB_LOCK: "STARTING",
    RUNNING: "MINING",
    PAUSED: "PAUSED",
    CANDIDATE_LOCAL: "CANDIDATE_FOUND",
    SUBMITTING: "SUBMITTING",
    SUBMIT_RETRY: "SUBMIT_RETRY",
    VERIFIED: "VERIFIED",
    EXPIRED: "EXPIRED",
    CANCELLED: snapshot.proof?.proofStatus === "CONSUMED" ? "EXPIRED" : "ERROR",
    ERROR: "ERROR",
  };
  const challengeContextMatches = !snapshot.challenge || Boolean(wallet.authenticated && wallet.account && activeToken &&
    wallet.chainId?.toString() === String(snapshot.challenge.chainId) && sameAddress(wallet.account, snapshot.challenge.walletAddress) &&
    sameAddress(activeToken, snapshot.challenge.tokenAddress));
  const displayPhase: MiningPhase = challengeContextMatches ? phaseMap[snapshot.phase] : "LOCKED";
  const session = snapshot.session;
  const sessionAction = !session ? undefined : session.status === "CHECKING" ? "blocked" :
    ["UNKNOWN", "LEGACY_CONFLICT"].includes(session.status) || session.message || snapshot.phase === "ERROR" ? "check" :
    !session.deploymentReady && !snapshot.proof ? "blocked" :
    session.status === "ACTIVE" ? session.lease === "IN_USE" ? "transfer" : "resume" :
    "new";
  return {
    phase: displayPhase,
    ...(sessionAction ? { sessionAction } : {}),
    sessionChecking: session?.status === "CHECKING",
    savedResultCount: challengeContextMatches ? snapshot.savedResults?.length ?? 0 : 0,
    hasSavedProof: snapshot.savedResults?.some(saved => saved.proofId && saved.challengeId !== snapshot.challenge?.challengeId) ?? false,
    hasSavedCandidate: snapshot.savedResults?.some(saved => !saved.proofId && saved.challengeId !== snapshot.challenge?.challengeId) ?? false,
    canCreateNewSession: Boolean(session?.deploymentReady && session.status === "SOLVED" && !session.message),
    mode: snapshot.mode,
    powerMode: snapshot.powerMode,
    ...(snapshot.cpu ? { cpu: snapshot.cpu } : {}),
    gpuAvailable: snapshot.gpu.status === "AVAILABLE" && snapshot.gpu.selfTested,
    gpuReason: snapshot.gpu.reason,
    ...(challengeContextMatches ? miningTelemetryView(snapshot) : { measuredHashrate: null, attempts: "—", activeElapsedMs: 0, estimatedTimeMs: null }),
    difficultyLabel: challengeContextMatches && snapshot.challenge ? targetDifficultyLabel(BigInt(snapshot.challenge.targetExclusive)) :
      globalDifficultyBits !== null ? `${globalDifficultyBits} bits` : "—",
    challengeId: challengeContextMatches ? snapshot.challenge?.challengeId ?? null : null,
    challengeExpiresAt: challengeContextMatches && snapshot.challenge ? Number(snapshot.challenge.expiresAt) : null,
    proofId: challengeContextMatches ? snapshot.proof?.proofId ?? null : null,
    statusMessage: session?.message ?? (session && snapshot.phase === "ERROR" ? "Mining paused. Check status before resuming." :
      session && !session.deploymentReady ? "Mining is disabled in this runtime. Saved sessions remain available to check." :
      session?.status === "ACTIVE" && snapshot.candidate && !snapshot.proof ? "Saved result is unconfirmed. Check status or resume to search for a new result." :
      session?.status === "ACTIVE" && session.lease === "IN_USE" ? "This wallet is mining in another tab or browser." :
      session?.status === "ACTIVE" && snapshot.phase !== "RUNNING" ? "Your mining session is ready to resume." :
      challengeContextMatches ? miningStatus(snapshot, enabled) : "Connect the authenticated proof-owner wallet on the challenge chain"),
    canRetrySubmission: challengeContextMatches && snapshot.phase === "SUBMIT_RETRY" && snapshot.candidate !== null &&
      (!session || snapshot.candidateCanRetry === true),
    demoOnly: false,
    logs: (challengeContextMatches ? snapshot.logs : []).map((entry, index) => ({ id: `${entry.atMs}-${index}`, at: entry.atMs,
      level: entry.code.includes("ERROR") || entry.code.includes("FAILED") ? "ERROR" :
        entry.code.includes("VERIFIED") ? "SUCCESS" : entry.code.includes("PAUSED") ? "WARNING" : "INFO",
      message: `${entry.code}${entry.detail ? ` · ${entry.detail}` : ""}` })),
  };
}

export function miningTelemetryView(snapshot: MiningSnapshot): Pick<MiningView,
  "measuredHashrate" | "attempts" | "activeElapsedMs" | "estimatedTimeMs"> {
  return {
    measuredHashrate: snapshot.hashesPerSecond === null ? null : formatHashrate(snapshot.hashesPerSecond),
    attempts: snapshot.attemptsText,
    activeElapsedMs: snapshot.activeElapsedMs,
    estimatedTimeMs: snapshot.phase === "RUNNING" ?
      estimateMiningTimeMs(snapshot.challenge?.targetExclusive ?? null, snapshot.hashesPerSecond) ?? snapshot.estimatedTimeMs ?? null :
      snapshot.estimatedTimeMs ?? null,
  };
}

function formatHashrate(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)} GH/s`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)} MH/s`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(2)} kH/s`;
  return `${Math.round(value)} H/s`;
}

function targetDifficultyLabel(target: bigint): string {
  if (target <= 0n) return "Invalid target";
  let bits = 0;
  let value = target;
  while (value > 1n) { value >>= 1n; bits += 1; }
  return target === 1n << BigInt(bits) ? `${256 - bits} bits` : "Custom integer target";
}

function miningStatus(snapshot: MiningSnapshot, enabled: boolean): string {
  if (!enabled && !snapshot.challenge) return "Mining is disabled by the server";
  switch (snapshot.phase) {
    case "RUNNING": return `${snapshot.mode} mining active`;
    case "CANDIDATE_LOCAL": return "Candidate found — verifying locally";
    case "SUBMITTING": return "Submitting candidate for server verification";
    case "SUBMIT_RETRY": return snapshot.candidate ?
      "Proof submission is unconfirmed. Retry sends the same saved nonce; no new mining is needed." :
      "Proof recovery is unavailable: no saved nonce was found for this challenge.";
    case "VERIFIED": return "Proof verified — choose an eligible tier";
    case "PAUSED": {
      switch (snapshot.pauseReason) {
        case "ANOTHER_TAB_IS_MINING": return "Another tab is mining this challenge. Pause it there, then resume here.";
        case "PAGE_HIDDEN": return "Mining paused while this page was hidden. Keep this page visible and resume.";
        case "RESTORED_CHECKPOINT": return "Saved mining restored. Resume to continue the same challenge.";
        case "WALLET_CONTEXT_CHANGED": return "Mining paused because the wallet, token or network changed. Review the context before resuming.";
        default: return snapshot.pauseReason ?? "Mining paused";
      }
    }
    case "EXPIRED": return snapshot.proof?.proofStatus === "EXPIRED" ? "The proof has expired. Request a new challenge." : "Challenge expired";
    case "CANCELLED": return snapshot.proof?.proofStatus === "CONSUMED" ? "The proof has been consumed. Request a new challenge." : "Challenge cancelled";
    case "ERROR": return snapshot.errorCode ?? "Mining error";
    default: return snapshot.challenge ? "Challenge ready" : "Ready for a server challenge";
  }
}
